import { i18n } from '@/i18n.js'
import { getTenantSettings, getTranslations } from '@/api/api.js'

/**
 * Loads the tenant's locale preference and any API-driven translation overrides,
 * then merges them into the i18n instance's `entity` namespace.
 *
 * The backend exposes:
 *   GET /api/translations/
 *   → { en: { project: "Event", project_plural: "Events" }, nb: { project: "Arrangement" } }
 *
 * These override the `entity.*` keys that are linked throughout the locale files
 * (e.g. nav.projects → @:entity.project_plural), so one API change propagates
 * automatically to nav labels, page titles, empty states, and toast messages.
 *
 * Tenant settings supply `default_locale` (added to the backend as part of this feature).
 */
export function useLocale() {
  async function loadOverridesAndLocale() {
    try {
      const [settings, overrides] = await Promise.all([
        getTenantSettings(),
        getTranslations(),
      ])

      // Apply locale from tenant settings (falls back to current value if missing)
      const locale = settings?.default_locale ?? i18n.global.locale.value
      if (locale && ['en', 'nb'].includes(locale)) {
        i18n.global.locale.value = locale
      }

      // Merge API entity overrides into each locale's `entity` namespace.
      // Only the whitelisted keys (project, project_plural, customer, …) come from
      // the API — this is enforced server-side via KeyEnum.
      for (const [loc, keys] of Object.entries(overrides ?? {})) {
        if (!keys || typeof keys !== 'object') continue
        const entityOverrides = {}
        for (const [key, value] of Object.entries(keys)) {
          if (typeof value === 'string') {
            entityOverrides[key] = value
          }
        }
        if (Object.keys(entityOverrides).length > 0) {
          i18n.global.mergeLocaleMessage(loc, { entity: entityOverrides })
        }
      }
    } catch (e) {
      // Non-fatal — app works correctly with bundled default translations
      console.warn('[i18n] Failed to load locale overrides:', e)
    }
  }

  return { loadOverridesAndLocale }
}
